import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// ---------------------------------------------------------------------
// NOTES PDF — "Generate PDF" for a saved Notes document.
//
// SELF-CONTAINED: this file imports nothing from Invoice.js, so Invoice.js
// is left completely untouched. The company details, PDF_THEME colors and
// the header/table helpers below are copied verbatim from the existing
// Invoice.js implementation so both PDFs look identical. If the company
// address or contact details ever change, update them in Invoice.js,
// Materials.js, Estimation1.js, Payments.js, Clients.js AND here.
//
// Read-only: nothing here touches Firestore, the notes collection, the
// editor, or any existing save/autosave path. It receives an already-
// loaded TipTap JSON document plus a few display strings, and turns them
// into a downloadable PDF.
//
// LAYOUT: the page is deliberately minimal — company header (name,
// address, phone, email, website), the note body flowed straight below
// the gold divider with no section heading of any kind, and a blank
// signing area with a bold "Authorized Signature" label that follows
// immediately after the content. Pages 2+ carry only the company name at
// the top left; there is no document title on the right.
//
// generateNotePdf() still accepts the full payload Notes.js builds; any
// property it no longer renders is simply ignored, so Notes.js needs no
// change at all.
//
// (If you prefer everything inside Notes.js, paste this whole file above
//  `function Notes()` in Notes.js, delete the two `import` lines at the
//  top — moving them into Notes.js's own import block — and remove the
//  `export` keyword on generateNotePdf. No other change is needed.)
// ---------------------------------------------------------------------

// ===================== Company branding (from Invoice.js) =====================

const COMPANY_NAME = "Sarrathi Constructions";
const COMPANY_ADDRESS =
  "82, Jagannathan Salai, Anjugam Nagar,SRP Colony, Kolathur, Chennai - 600082.";
const LOGO_PATH = "/logo/company logo.jpeg";

const COMPANY_EMAIL = "build@sarrathiconstructions.com";
const COMPANY_PHONE = "98405 24873, 98407 98059";
const COMPANY_WEBSITE = "www.sarrathiconstructions.com";

const PDF_THEME = {
  colors: {
    primaryDark: [23, 32, 51],   // headings / titles
    primary: [22, 37, 61],       // table header fill
    accentGold: [199, 156, 68],  // divider line under header
    textMuted: [110, 118, 132],
    textBody: [55, 65, 81],
    borderLight: [225, 228, 233],
    rowStripe: [246, 247, 250],
    balanceRed: [196, 78, 66],
    paidGreen: [46, 138, 89],
  },
  fontFamily: "helvetica",
};

// Loads the company logo as a base64 data URL so jsPDF can embed it.
// Read-only, local static asset fetch — no Firestore access involved.
const loadCompanyLogoDataUrl = async () => {
  try {
    const res = await fetch(LOGO_PATH);
    if (!res.ok) throw new Error("Logo not found");
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (err) {
    console.error("Failed to load company logo for PDF:", err);
    return null;
  }
};

// Turns a document title into a safe, non-generic PDF filename.
const sanitizeFileName = (name) => {
  const cleaned = (name || "").trim().replace(/[^a-zA-Z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  return cleaned || "Note";
};

// Header — logo + company name/address/phone/email/website on the left,
// gold divider underneath. Same three contact lines used by the Invoice,
// Estimation, Materials and Payments PDFs, so branding stays identical
// across every document the app generates.
const drawPdfHeader = (docPdf, { logoDataUrl, pageWidth, marginX }) => {
  const { colors } = PDF_THEME;
  const headerTop = 40;
  const logoSize = 54;

  if (logoDataUrl) {
    try {
      docPdf.addImage(logoDataUrl, "JPEG", marginX, headerTop, logoSize, logoSize);
    } catch (imgErr) {
      console.error("Failed to draw logo on PDF:", imgErr);
    }
  }

  const textX = marginX + logoSize + 16;
  const nameY = headerTop + logoSize / 2 - 6;
  const addressMaxWidth = pageWidth - marginX * 2 - logoSize - 16;

  docPdf.setFont(PDF_THEME.fontFamily, "bold");
  docPdf.setFontSize(17);
  docPdf.setTextColor(...colors.primaryDark);
  docPdf.text(COMPANY_NAME, textX, nameY);

  docPdf.setFont(PDF_THEME.fontFamily, "normal");
  docPdf.setFontSize(9);
  docPdf.setTextColor(...colors.textMuted);
  docPdf.text(COMPANY_ADDRESS, textX, nameY + 15, { maxWidth: addressMaxWidth });
  docPdf.text(`Phone: ${COMPANY_PHONE}`, textX, nameY + 27);
  docPdf.text(`Email: ${COMPANY_EMAIL}`, textX, nameY + 39);
  docPdf.text(`Website: ${COMPANY_WEBSITE}`, textX, nameY + 51);

  const headerBottom = Math.max(headerTop + logoSize, nameY + 51) + 16;

  docPdf.setDrawColor(...colors.accentGold);
  docPdf.setLineWidth(1.3);
  docPdf.line(marginX, headerBottom, pageWidth - marginX, headerBottom);

  return headerBottom + 22;
};

// Shared table look — grid borders, consistent padding, dark-navy header,
// subtle row striping.
const baseTableOptions = () => ({
  theme: "grid",
  styles: {
    font: PDF_THEME.fontFamily,
    fontSize: 9,
    cellPadding: { top: 7, bottom: 7, left: 8, right: 8 },
    textColor: PDF_THEME.colors.textBody,
    lineColor: PDF_THEME.colors.borderLight,
    lineWidth: 0.6,
    valign: "middle",
  },
  headStyles: {
    fillColor: PDF_THEME.colors.primary,
    textColor: 255,
    fontStyle: "bold",
    fontSize: 9,
    halign: "left",
  },
  alternateRowStyles: { fillColor: PDF_THEME.colors.rowStripe },
});

// ===================== Layout constants =====================

const MARGIN_X = 40;

// Vertical band reserved at the top of pages 2+ for the slim repeated
// header drawn in the final pass. Content (both flowed text and any
// autoTable that breaks across a page) always starts below this, so a
// long note can never render underneath the repeated header. Matches the
// PDF_HEADER_BAND value used by the Materials/Estimation reports.
const CONTENT_TOP_SUBSEQUENT = 74;

// Space kept clear at the bottom of every page for the footer lines.
const FOOTER_RESERVE = 62;

// Blank vertical space left between the last line of note content and
// the "Authorized Signature" label — this is the room actually signed,
// stamped or sealed by hand.
const SIGNATURE_TOP_GAP = 54;

const BLOCK_SPACING = 8;

// jsPDF's standard fonts are WinAnsi-encoded and have no glyph for "₹"
// (U+20B9) — exactly the reason Invoice.js uses an ASCII "Rs." prefix in
// its PDFs. Notes content is free text typed by the admin, so the same
// substitution (plus a few typographic characters) is applied here. The
// note itself in Firestore is never modified.
const toPdfSafeText = (value) =>
  String(value ?? "")
    .replace(/\u20B9\s*/g, "Rs. ")
    .replace(/[\u2018\u2019\u201A\u201B]/g, "'")
    .replace(/[\u201C\u201D\u201E]/g, '"')
    .replace(/[\u2013\u2014]/g, "-")
    .replace(/\u2026/g, "...")
    .replace(/\u00A0/g, " ")
    .replace(/\r\n/g, "\n");

// ===================== TipTap JSON helpers =====================

// Flattens any node to plain text. Block-level containers join their
// children with newlines so a multi-paragraph table cell keeps its shape.
const flattenText = (node) => {
  if (!node) return "";
  if (node.type === "text") return node.text || "";
  if (node.type === "hardBreak") return "\n";

  const children = (node.content || []).map(flattenText);

  const isBlockContainer =
    node.type === "doc" ||
    node.type === "tableCell" ||
    node.type === "tableHeader" ||
    node.type === "listItem" ||
    node.type === "blockquote";

  return isBlockContainer ? children.join("\n") : children.join("");
};

// Turns a block node's inline content into a flat list of styled runs.
// `{ lineBreak: true }` entries represent hard breaks.
const extractInlineSegments = (node) => {
  const segments = [];

  const walk = (n, marks) => {
    if (!n) return;

    if (n.type === "text") {
      const style = { ...marks };
      (n.marks || []).forEach((mark) => {
        if (mark.type === "bold") style.bold = true;
        if (mark.type === "italic") style.italic = true;
        if (mark.type === "underline") style.underline = true;
        if (mark.type === "strike") style.strike = true;
        if (mark.type === "code") style.code = true;
      });

      const safe = toPdfSafeText(n.text || "");
      safe.split("\n").forEach((chunk, idx) => {
        if (idx > 0) segments.push({ lineBreak: true });
        if (chunk) segments.push({ text: chunk, ...style });
      });
      return;
    }

    if (n.type === "hardBreak") {
      segments.push({ lineBreak: true });
      return;
    }

    (n.content || []).forEach((child) => walk(child, marks));
  };

  (node.content || []).forEach((child) => walk(child, {}));
  return segments;
};

// ===================== Inline text layout =====================
// (mixed bold / italic / underline / strike / code within one wrapped
//  paragraph)

const applyInlineFont = (docPdf, seg, base) => {
  const family = seg.code ? "courier" : PDF_THEME.fontFamily;
  const bold = !!(seg.bold || base.bold);
  const italic = !!(seg.italic || base.italic);

  let style = "normal";
  if (bold && italic) style = "bolditalic";
  else if (bold) style = "bold";
  else if (italic) style = "italic";

  docPdf.setFont(family, style);
  docPdf.setFontSize(base.fontSize);
  docPdf.setTextColor(...(base.color || PDF_THEME.colors.textBody));
};

// Greedy word wrap that measures every token with its own font, so a bold
// word and a normal word on the same line are both sized correctly.
const buildLines = (docPdf, segments, base, maxWidth) => {
  const lines = [];
  let current = { parts: [], width: 0 };

  const pushLine = () => {
    lines.push(current);
    current = { parts: [], width: 0 };
  };

  const trimTrailingSpace = () => {
    while (current.parts.length && /^\s+$/.test(current.parts[current.parts.length - 1].text)) {
      const removed = current.parts.pop();
      current.width -= removed.width;
    }
  };

  segments.forEach((seg) => {
    if (seg.lineBreak) {
      trimTrailingSpace();
      pushLine();
      return;
    }

    const tokens = (seg.text || "").split(/(\s+)/).filter((t) => t !== "");

    tokens.forEach((token) => {
      const isSpace = /^\s+$/.test(token);
      const text = isSpace ? " " : token;

      applyInlineFont(docPdf, seg, base);
      const width = docPdf.getTextWidth(text);

      if (isSpace) {
        if (current.parts.length === 0) return; // no leading spaces on a line
        current.parts.push({ text, seg, width });
        current.width += width;
        return;
      }

      if (current.width + width > maxWidth && current.parts.length > 0) {
        trimTrailingSpace();
        pushLine();
      }

      // A single unbroken token longer than the column (a URL, a long
      // reference number) is split by character rather than overflowing.
      if (width > maxWidth) {
        let chunk = "";
        Array.from(text).forEach((ch) => {
          applyInlineFont(docPdf, seg, base);
          if (docPdf.getTextWidth(chunk + ch) > maxWidth && chunk) {
            const chunkWidth = docPdf.getTextWidth(chunk);
            current.parts.push({ text: chunk, seg, width: chunkWidth });
            current.width += chunkWidth;
            pushLine();
            chunk = ch;
          } else {
            chunk += ch;
          }
        });
        if (chunk) {
          applyInlineFont(docPdf, seg, base);
          const chunkWidth = docPdf.getTextWidth(chunk);
          current.parts.push({ text: chunk, seg, width: chunkWidth });
          current.width += chunkWidth;
        }
        return;
      }

      current.parts.push({ text, seg, width });
      current.width += width;
    });
  });

  trimTrailingSpace();
  pushLine();
  return lines;
};

// ===================== Page flow =====================

const ensureSpace = (state, needed) => {
  if (state.y + needed > state.pageHeight - FOOTER_RESERVE) {
    state.docPdf.addPage();
    state.y = CONTENT_TOP_SUBSEQUENT;
    return true;
  }
  return false;
};

const drawLines = (state, lines, base, { x, maxWidth, align, lineHeight }) => {
  const { docPdf } = state;

  lines.forEach((line) => {
    ensureSpace(state, lineHeight);

    let cursorX = x;
    if (align === "center") cursorX = x + (maxWidth - line.width) / 2;
    else if (align === "right") cursorX = x + maxWidth - line.width;

    line.parts.forEach((part) => {
      applyInlineFont(docPdf, part.seg, base);
      docPdf.text(part.text, cursorX, state.y);

      if (part.seg.underline || part.seg.strike) {
        const decorationY = part.seg.strike
          ? state.y - base.fontSize * 0.28
          : state.y + 1.8;
        docPdf.setDrawColor(...(base.color || PDF_THEME.colors.textBody));
        docPdf.setLineWidth(0.6);
        docPdf.line(cursorX, decorationY, cursorX + part.width, decorationY);
      }

      cursorX += part.width;
    });

    state.y += lineHeight;
  });
};

// ===================== Block rendering =====================

const HEADING_SIZES = { 1: 16, 2: 13.5, 3: 11.5 };
const HEADING_LINE_HEIGHTS = { 1: 22, 2: 19, 3: 16.5 };

const renderTableNode = (state, node) => {
  const { docPdf } = state;

  const rows = (node.content || []).filter((r) => r.type === "tableRow");
  if (rows.length === 0) return;

  const toCell = (cell) => ({
    content: toPdfSafeText(flattenText(cell)),
    colSpan: Number(cell.attrs?.colspan) || 1,
    rowSpan: Number(cell.attrs?.rowspan) || 1,
  });

  const firstCells = rows[0].content || [];
  const hasHeaderRow =
    firstCells.length > 0 && firstCells.every((c) => c.type === "tableHeader");

  const head = hasHeaderRow ? [firstCells.map(toCell)] : undefined;
  const bodyRows = hasHeaderRow ? rows.slice(1) : rows;
  const body = bodyRows.map((row) => (row.content || []).map(toCell));

  ensureSpace(state, 46);

  autoTable(docPdf, {
    ...baseTableOptions(),
    startY: state.y,
    head,
    body,
    styles: { ...baseTableOptions().styles, fontSize: 9 },
    margin: { left: MARGIN_X, right: MARGIN_X, top: CONTENT_TOP_SUBSEQUENT },
  });

  state.y = docPdf.lastAutoTable.finalY + 16;
};

const renderListItem = (state, item, { indent, marker }) => {
  const { docPdf } = state;

  // Reserve the first line up front so the marker and the text it belongs
  // to can never be separated by a page break.
  ensureSpace(state, 17);

  docPdf.setFont(PDF_THEME.fontFamily, "bold");
  docPdf.setFontSize(10.5);
  docPdf.setTextColor(...PDF_THEME.colors.textMuted);
  docPdf.text(marker, MARGIN_X + indent - 16, state.y);

  (item.content || []).forEach((child) => renderBlock(state, child, { indent }));
};

function renderBlock(state, node, ctx = {}) {
  if (!node) return;

  const { docPdf } = state;
  const indent = ctx.indent || 0;
  const x = MARGIN_X + indent;
  const maxWidth = state.pageWidth - MARGIN_X * 2 - indent;

  switch (node.type) {
    case "paragraph": {
      const base = { fontSize: 10.5, color: PDF_THEME.colors.textBody };
      const lines = buildLines(docPdf, extractInlineSegments(node), base, maxWidth);
      drawLines(state, lines, base, {
        x,
        maxWidth,
        align: node.attrs?.textAlign || "left",
        lineHeight: 15.5,
      });
      state.y += BLOCK_SPACING;
      break;
    }

    case "heading": {
      const level = Math.min(Math.max(Number(node.attrs?.level) || 1, 1), 3);
      const base = {
        fontSize: HEADING_SIZES[level],
        bold: true,
        color: PDF_THEME.colors.primaryDark,
      };
      const lines = buildLines(docPdf, extractInlineSegments(node), base, maxWidth);

      state.y += level === 1 ? 10 : 7;
      drawLines(state, lines, base, {
        x,
        maxWidth,
        align: node.attrs?.textAlign || "left",
        lineHeight: HEADING_LINE_HEIGHTS[level],
      });
      state.y += 6;
      break;
    }

    case "bulletList":
    case "orderedList": {
      const start = Number(node.attrs?.start) || 1;
      (node.content || []).forEach((item, idx) => {
        const marker = node.type === "bulletList" ? "\u2022" : `${start + idx}.`;
        renderListItem(state, item, { indent: indent + 18, marker });
      });
      state.y += 2;
      break;
    }

    case "listItem": {
      (node.content || []).forEach((child) => renderBlock(state, child, { indent }));
      break;
    }

    case "blockquote": {
      const startPage = docPdf.internal.getCurrentPageInfo().pageNumber;
      const startY = state.y - 10;

      (node.content || []).forEach((child) =>
        renderBlock(state, child, { indent: indent + 14 }),
      );

      const endPage = docPdf.internal.getCurrentPageInfo().pageNumber;
      if (endPage === startPage) {
        docPdf.setDrawColor(...PDF_THEME.colors.accentGold);
        docPdf.setLineWidth(2);
        docPdf.line(x + 2, startY, x + 2, state.y - BLOCK_SPACING - 4);
      }
      break;
    }

    case "codeBlock": {
      const base = { fontSize: 9, color: PDF_THEME.colors.textBody };
      const segments = [];
      toPdfSafeText(flattenText(node))
        .split("\n")
        .forEach((line, idx) => {
          if (idx > 0) segments.push({ lineBreak: true });
          if (line) segments.push({ text: line, code: true });
        });

      const lines = buildLines(docPdf, segments, base, maxWidth - 16);
      ensureSpace(state, 15);
      drawLines(state, lines, base, {
        x: x + 8,
        maxWidth: maxWidth - 16,
        align: "left",
        lineHeight: 13,
      });
      state.y += BLOCK_SPACING;
      break;
    }

    case "horizontalRule": {
      ensureSpace(state, 18);
      docPdf.setDrawColor(...PDF_THEME.colors.borderLight);
      docPdf.setLineWidth(0.8);
      docPdf.line(x, state.y - 4, x + maxWidth, state.y - 4);
      state.y += 14;
      break;
    }

    case "table": {
      renderTableNode(state, node);
      break;
    }

    default: {
      // Unknown/future node types still render their children rather than
      // silently dropping the user's content.
      if (Array.isArray(node.content)) {
        node.content.forEach((child) => renderBlock(state, child, ctx));
      }
      break;
    }
  }
}

// ===================== Authorized signature =====================
//
// Drawn once, immediately AFTER the note content — it is NOT pinned to
// the bottom of the page. A short note therefore gets its signature area
// just below the last line, with SIGNATURE_TOP_GAP of blank space above
// the label for signing, stamping or sealing by hand.
//
// The only time it moves is when that gap plus the label genuinely will
// not fit above the footer on the current page; then, and only then, a
// fresh page is added. Because this runs before the footer/page-numbering
// pass below, any page it adds is still counted correctly in
// "Page X of Y".
// ---------------------------------------------------------------------
const drawSignatureBlock = (state) => {
  const { docPdf, pageWidth, pageHeight } = state;
  const { colors } = PDF_THEME;

  const labelY = state.y + SIGNATURE_TOP_GAP;

  if (labelY > pageHeight - FOOTER_RESERVE) {
    docPdf.addPage();
    state.y = CONTENT_TOP_SUBSEQUENT + SIGNATURE_TOP_GAP;
  } else {
    state.y = labelY;
  }

  docPdf.setFont(PDF_THEME.fontFamily, "bold");
  docPdf.setFontSize(10);
  docPdf.setTextColor(...colors.primaryDark);
  docPdf.text("Authorized Signature", pageWidth - MARGIN_X, state.y, {
    align: "right",
  });

  state.y += 14;
};

// ===================== Public API =====================

/**
 * Builds and downloads a Notes document as a PDF.
 *
 * All arguments are plain display values resolved by the caller
 * (Notes.js) — this module never reads Firestore itself. Only `title`
 * (used for the filename) and `content` are rendered; any other property
 * Notes.js passes is accepted and ignored.
 */
export const generateNotePdf = async ({ title, content }) => {
  const { colors } = PDF_THEME;

  const docPdf = new jsPDF({ unit: "pt", format: "a4" });
  const pageWidth = docPdf.internal.pageSize.getWidth();
  const pageHeight = docPdf.internal.pageSize.getHeight();

  const safeTitle = toPdfSafeText(title || "").trim() || "Untitled document";

  // ---- Company header (same structure as the Invoice PDFs) ----
  const logoDataUrl = await loadCompanyLogoDataUrl();

  const contentTop = drawPdfHeader(docPdf, {
    logoDataUrl,
    pageWidth,
    marginX: MARGIN_X,
  });

  // ---- Note content (flowed, auto-paginating, no section heading) ----
  const state = { docPdf, pageWidth, pageHeight, y: contentTop };
  const blocks = Array.isArray(content?.content) ? content.content : [];

  if (blocks.length === 0) {
    docPdf.setFont(PDF_THEME.fontFamily, "italic");
    docPdf.setFontSize(9.5);
    docPdf.setTextColor(...colors.textMuted);
    docPdf.text("This note has no content yet.", MARGIN_X, state.y);
    state.y += 16;
  } else {
    blocks.forEach((block) => renderBlock(state, block, { indent: 0 }));
  }

  // ---- Authorized signature (directly below the content) ----
  drawSignatureBlock(state);

  // ---- Repeated slim header (pages 2+) and footer (every page) ----
  const pageCount = docPdf.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i += 1) {
    docPdf.setPage(i);

    if (i > 1) {
      docPdf.setFont(PDF_THEME.fontFamily, "bold");
      docPdf.setFontSize(11);
      docPdf.setTextColor(...colors.primaryDark);
      docPdf.text(COMPANY_NAME, MARGIN_X, 30);

      docPdf.setDrawColor(...colors.accentGold);
      docPdf.setLineWidth(1);
      docPdf.line(MARGIN_X, 44, pageWidth - MARGIN_X, 44);
    }

    docPdf.setFont(PDF_THEME.fontFamily, "italic");
    docPdf.setFontSize(8.5);
    docPdf.setTextColor(...colors.textMuted);
    docPdf.text("Thank you for choosing Sarrathi Constructions.", MARGIN_X, pageHeight - 30);
    docPdf.text("This document is system generated.", MARGIN_X, pageHeight - 18);
    docPdf.text(`Page ${i} of ${pageCount}`, pageWidth - MARGIN_X, pageHeight - 18, {
      align: "right",
    });
  }

  docPdf.save(`${sanitizeFileName(safeTitle)}_Note.pdf`);
};

export default generateNotePdf;